import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/videoData';

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500">
              <i className="ri-play-circle-fill text-background-50 text-lg"></i>
            </span>
            <span className={`font-heading text-xl font-bold tracking-tight ${scrolled ? 'text-foreground-900' : 'text-background-50'}`}>
              {t('site_name')}
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                isActive('/')
                  ? scrolled ? 'bg-primary-100 text-primary-700' : 'bg-background-50/20 text-background-50'
                  : scrolled ? 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100' : 'text-background-50/80 hover:text-background-50 hover:bg-background-50/10'
              }`}
            >
              {t('home')}
            </Link>
            {categories.slice(0, 6).map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive(`/category/${cat.slug}`)
                    ? scrolled ? 'bg-primary-100 text-primary-700' : 'bg-background-50/20 text-background-50'
                    : scrolled ? 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100' : 'text-background-50/80 hover:text-background-50 hover:bg-background-50/10'
                }`}
              >
                {cat.name}
              </Link>
            ))}
            <Link
              to="/about"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                isActive('/about')
                  ? scrolled ? 'bg-primary-100 text-primary-700' : 'bg-background-50/20 text-background-50'
                  : scrolled ? 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100' : 'text-background-50/80 hover:text-background-50 hover:bg-background-50/10'
              }`}
            >
              {t('about')}
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <button className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${scrolled ? 'text-foreground-500 hover:text-foreground-800 hover:bg-background-100' : 'text-background-50/80 hover:text-background-50 hover:bg-background-50/10'}`}>
              <i className="ri-search-line text-lg"></i>
            </button>
            <Link
              to="/"
              className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t('hero_cta')}
            </Link>
          </div>

          <button
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer ${scrolled ? 'text-foreground-700' : 'text-background-50'}`}
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 shadow-lg">
          <div className="px-4 py-3 flex flex-col gap-1">
            <Link to="/" className={`px-4 py-3 rounded-lg text-sm font-medium ${isActive('/') ? 'bg-primary-50 text-primary-700' : 'text-foreground-700 hover:bg-background-100'}`}>{t('home')}</Link>
            {categories.map((cat) => (
              <Link key={cat.id} to={`/category/${cat.slug}`} className={`px-4 py-3 rounded-lg text-sm font-medium ${isActive(`/category/${cat.slug}`) ? 'bg-primary-50 text-primary-700' : 'text-foreground-700 hover:bg-background-100'}`}>{cat.name}</Link>
            ))}
            <Link to="/about" className={`px-4 py-3 rounded-lg text-sm font-medium ${isActive('/about') ? 'bg-primary-50 text-primary-700' : 'text-foreground-700 hover:bg-background-100'}`}>{t('about')}</Link>
          </div>
        </div>
      )}
    </nav>
  );
}